// «Prueba en vivo» (owner request 2026-11-03; context/v1.1 «prueba conectada con un registro de prueba»): the team plays
// the prospect inside the app while everything else LIA does is real — CRM stages, tasks, notes, booking — on an
// existing CRM lead or a new one. Messages never leave the app. Browser-safe.
import { z } from 'zod';

const Id = z.string().min(1).max(64);

export const LiveTestLeadSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('existing'),
    connectionId: Id,
    crmContactId: z.string().min(1).max(64),
    /** The deal/opportunity to work on (null = LIA finds or creates it by the mapping). */
    crmDealId: z.string().min(1).max(64).nullable().default(null),
  }),
  z.object({
    mode: z.literal('new'),
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().email().max(254).nullable().default(null),
    phone: z.string().trim().regex(/^\+[1-9]\d{7,14}$/, 'Número en formato internacional, por ejemplo +525512345678').nullable().default(null),
    /** Create the contact (and the deal, when the mapping has a pipeline for the project) in this CRM. null = LIA only. */
    connectionId: Id.nullable().default(null),
  }),
]);
export type LiveTestLead = z.infer<typeof LiveTestLeadSchema>;

export const StartLiveTestSchema = z.object({ projectId: Id, lead: LiveTestLeadSchema });
export type StartLiveTest = z.infer<typeof StartLiveTestSchema>;

export const LiveTestSchema = z.object({
  /** The test conversation id (the chat shown in the app). */
  id: z.string(),
  contactId: z.string(),
  contactName: z.string().nullable(),
  leadInterestId: z.string(),
  projectId: z.string(),
  projectName: z.string().nullable(),
  stage: z.string(),
  crm: z
    .object({
      connectionId: z.string(),
      provider: z.string(),
      contactId: z.string().nullable(),
      dealId: z.string().nullable(),
      dealUrl: z.string().nullable(),
    })
    .nullable(),
  ended: z.boolean(),
  startedAt: z.string(),
});
export type LiveTest = z.infer<typeof LiveTestSchema>;
export const LiveTestListSchema = z.object({ items: z.array(LiveTestSchema) });

export const LiveTestMessageSchema = z.object({ text: z.string().trim().min(1).max(2000) });

export const LiveTestAdvanceResultSchema = z.object({
  /** false = there was nothing scheduled to run now (detail says why). */
  ran: z.boolean(),
  detail: z.string(),
});

export const CrmContactSearchQuerySchema = z.object({ q: z.string().trim().min(2).max(100) });
export const CrmContactHitSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  ownerId: z.string().nullable(),
  /** Already linked to a LIA contact (cannot be used for a live test). */
  linked: z.boolean(),
  deals: z.array(z.object({ id: z.string(), name: z.string().nullable(), stage: z.string().nullable(), pipeline: z.string().nullable(), ownerId: z.string().nullable() })),
});
export const CrmContactHitsSchema = z.object({ items: z.array(CrmContactHitSchema) });
